import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, ChevronDown, Download, Loader2, RotateCcw, Sparkles } from "lucide-react";
import CreatorShell from "@/components/creator/CreatorShell";
import CreatorPlayer from "@/components/creator/CreatorPlayer";
import SongCover from "@/components/creator/SongCover";
import useCreatorSong from "@/components/creator/useCreatorSong";
import { downloadBlob, listSongs, patchSong, safeFilename } from "@/lib/creatorApi";
import { MASTER_USES, EXPORT_FORMATS, masterUseByKey } from "@/lib/creatorConstants";
import { getCurrentSongId } from "@/lib/studioMode";

function SongPicker() {
  const [songs, setSongs] = useState(null);
  useEffect(() => { listSongs({ limit: 30 }).then(setSongs).catch(() => setSongs([])); }, []);
  return (
    <CreatorShell testId="master-picker-page" back="/create" spine="master" title="Which song should we master?" subtitle="Pick one of your songs — or create a new one first.">
      {songs === null ? <Loader2 className="h-5 w-5 animate-spin text-white/60" /> : songs.length === 0 ? (
        <Link to="/create/song" data-testid="master-picker-create" className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-3 font-display font-semibold">Create a song first <ArrowRight className="h-4 w-4" /></Link>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="master-picker-list">
          {songs.map((s) => (
            <Link key={s.id} to={`/create/master/${s.id}`} data-testid={`master-pick-${s.id}`} className="flex items-center gap-3 rounded-2xl border border-white/[0.08] bg-[#0a0d1c]/80 p-3 transition-colors hover:bg-white/[0.06]">
              <SongCover song={s} size="sm" />
              <span className="min-w-0"><span className="block truncate font-display text-sm font-semibold">{s.title}</span><span className="block text-[11px] text-white/45">{s.creator_meta?.style_label || s.style?.split(",")[0] || "Song"}</span></span>
            </Link>
          ))}
        </div>
      )}
    </CreatorShell>
  );
}

const fmtDb = (v) => (typeof v === "number" && Number.isFinite(v) ? `${v > 0 ? "+" : ""}${v.toFixed(1)}` : "—");

/** Mastering by use, not by numbers. */
export default function MasterPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const current = getCurrentSongId();
  useEffect(() => { if (!id && current) navigate(`/create/master/${current}`, { replace: true }); }, [id, current, navigate]);
  if (!id) return current ? null : <SongPicker />;
  return <MasterSong id={id} />;
}

function MasterSong({ id }) {
  const { engine, song, setSong, error, channel, loadState } = useCreatorSong(id);
  const [useKey, setUseKey] = useState(song?.master?.use || null);
  const [busy, setBusy] = useState(null);
  const [stats, setStats] = useState(null);
  const [ab, setAb] = useState("after");
  const [advOpen, setAdvOpen] = useState(false);
  const [custom, setCustom] = useState({ lufs: -14, ceiling: -1.0, format: "wav24" });
  const use = useMemo(() => (useKey ? masterUseByKey(useKey) : null), [useKey]);

  useEffect(() => {
    if (use) setCustom((c) => ({ ...c, lufs: use.lufs, ceiling: use.ceiling }));
  }, [use]);

  const masterRecord = (out, key, format) => ({
    use: key || "custom", label: out.target.label, lufs_before: +out.before.lufs.toFixed(1), lufs_after: +out.after.lufs.toFixed(1),
    true_peak: +out.after.truePeakDb.toFixed(1), target_lufs: out.target.lufs, format: format || null, at: new Date().toISOString(),
  });

  const runMaster = async (target, key) => {
    if (loadState !== "ready" || engine.maxDuration <= 0) { toast.error("Almost there", { description: "Wait for the song to finish loading, then choose again." }); return; }
    setBusy(key);
    try {
      const out = await engine.runMastering(target);
      setStats(out);
      setAb("after");
      // Remember the choice on the song right away — not only when exported.
      if (song?.id) {
        patchSong(song.id, { master: masterRecord(out, key, null) }).then(setSong)
          .catch(() => toast.message("Mastered, but not saved yet", { description: "Check your connection — exporting will save it again." }));
      }
      toast.success(`Ready for ${target.label}`, { description: `Loudness ${fmtDb(out.before.lufs)} → ${fmtDb(out.after.lufs)} · peaks kept safe at ${fmtDb(out.after.truePeakDb)} dB · saved to your song` });
    } catch (e) {
      toast.error("Mastering didn't finish", { description: e?.message || "Try again." });
    } finally { setBusy(null); }
  };

  const pickUse = (u) => { setUseKey(u.key); runMaster({ label: u.label, lufs: u.lufs, ceiling: u.ceiling }, u.key); };
  const applyCustom = () => runMaster({ label: `${use?.label || "Custom"} · custom`, lufs: Number(custom.lufs), ceiling: Number(custom.ceiling) }, "custom");

  const toggleAb = (which) => {
    if (!stats) return;
    setAb(which);
    if (which === "before") engine.masteringChain.reset();
    else engine.masteringChain.apply(stats.recipe.params);
  };

  const keepOriginal = () => { engine.resetMastering(); setStats(null); setUseKey(null); toast.message("Original sound kept", { description: "No mastering is applied." }); };

  const exportMaster = async () => {
    if (!stats) return;
    setBusy("export");
    try {
      if (ab === "before") { engine.masteringChain.apply(stats.recipe.params); setAb("after"); }
      const fmt = EXPORT_FORMATS.find((f) => f.key === custom.format) || EXPORT_FORMATS[0];
      const blob = await engine.exportWav({ bitDepth: fmt.bitDepth });
      downloadBlob(blob, safeFilename(`${song.title} - ${stats.target.label}`, "wav"));
      patchSong(song.id, { master: masterRecord(stats, useKey, fmt.label) }).then(setSong)
        .catch(() => toast.message("Exported, but the master wasn't saved to your song", { description: "Check your connection and try again." }));
      toast.success("Mastered song exported", { description: `${fmt.label} · ${(blob.size / 1024 / 1024).toFixed(1)} MB` });
    } catch (e) {
      toast.error("Export failed", { description: e?.message || "Try again." });
    } finally { setBusy(null); }
  };

  if (error) {
    return <CreatorShell testId="master-page" back="/create/projects" title="Hmm, that song isn't here" subtitle={error} />;
  }

  return (
    <CreatorShell testId="master-page" back={`/create/song/${id}`} spine="master" spineDone={["write", "generate", ...(song?.vocal ? ["record"] : []), ...(song?.stems ? ["mix"] : [])]} title="How will you use your song?" subtitle="Choose one. ELPM sets the loudness, peak level and file format for you.">
      {!song ? <Loader2 className="h-5 w-5 animate-spin text-white/60" /> : (
        <div className="grid grid-cols-[minmax(0,1fr)] gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <div data-testid="master-uses" className="grid grid-cols-[minmax(0,1fr)] gap-3 sm:grid-cols-2">
              {MASTER_USES.map((u) => {
                const active = useKey === u.key;
                return (
                  <button
                    key={u.key}
                    type="button"
                    data-testid={`master-use-${u.key}`}
                    aria-pressed={active}
                    disabled={!!busy}
                    onClick={() => pickUse(u)}
                    className={`group relative overflow-hidden rounded-3xl border p-5 text-left transition-all duration-300 disabled:opacity-60 ${active ? "border-transparent bg-white/[0.06] shadow-[0_0_30px_rgba(99,102,241,0.25)]" : "border-white/[0.08] bg-[#0a0d1c]/80 hover:-translate-y-1"}`}
                  >
                    <span className={`pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-gradient-to-br ${u.accent} blur-3xl transition-opacity duration-500 ${active ? "opacity-50" : "opacity-0 group-hover:opacity-30"}`} />
                    <span className="flex items-center justify-between">
                      <span className="font-display text-base font-semibold">{u.label}</span>
                      {busy === u.key ? <Loader2 className="h-4 w-4 animate-spin" /> : active && stats ? <Check className="h-4 w-4 text-emerald-300" /> : null}
                    </span>
                    <span className="mt-1 block text-[12px] leading-relaxed text-white/55">{u.desc}</span>
                  </button>
                );
              })}
            </div>

            {stats ? (
              <div data-testid="master-result" className="mt-6 rounded-3xl border border-emerald-400/25 bg-emerald-500/[0.06] p-5">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-emerald-200"><Sparkles className="h-3.5 w-3.5" /> Ready for {stats.target.label}</p>
                <p className="mt-2 text-sm text-white/75">
                  {stats.after.lufs > stats.before.lufs + 0.3 ? `Your song is now ${fmtDb(stats.after.lufs - stats.before.lufs)} dB louder and ` : stats.after.lufs < stats.before.lufs - 0.3 ? `Your song was gently turned down ${fmtDb(stats.before.lufs - stats.after.lufs)} dB and ` : "Your song's level was already right, and "}
                  its peaks are kept safe — no clipping, no distortion.
                </p>
                <div className="mt-4 flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">Preview</span>
                  <div role="radiogroup" className="inline-flex rounded-full border border-white/10 bg-black/40 p-1">
                    {["before", "after"].map((w) => (
                      <button key={w} type="button" role="radio" aria-checked={ab === w} data-testid={`master-ab-${w}`} onClick={() => toggleAb(w)} className={`rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] transition-colors ${ab === w ? "bg-gradient-to-r from-blue-500 to-violet-600 text-white" : "text-white/55 hover:text-white"}`}>{w}</button>
                    ))}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="mt-6">
              <CreatorPlayer engine={engine} channel={channel} loadState={loadState} />
            </div>

            <div className="mt-6 rounded-3xl border border-white/[0.08] bg-black/25">
              <button type="button" data-testid="master-advanced-toggle" aria-expanded={advOpen} onClick={() => setAdvOpen((o) => !o)} className="flex w-full items-center justify-between px-5 py-4 text-left">
                <span className="font-display text-sm font-semibold text-white/80">Advanced Mastering Settings</span>
                <ChevronDown className={`h-4 w-4 text-white/60 transition-transform duration-300 ${advOpen ? "rotate-180" : ""}`} />
              </button>
              {advOpen ? (
                <div data-testid="master-advanced-body" className="grid gap-4 border-t border-white/[0.06] px-5 py-5 sm:grid-cols-3">
                  <label className="block">
                    <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">Mastering loudness (LUFS)</span>
                    <input data-testid="master-adv-lufs" type="number" step={0.5} min={-24} max={-6} value={custom.lufs} onChange={(e) => setCustom((c) => ({ ...c, lufs: e.target.value }))} className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-white focus:border-blue-400/60 focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">True peak (dB)</span>
                    <input data-testid="master-adv-peak" type="number" step={0.1} min={-3} max={0} value={custom.ceiling} onChange={(e) => setCustom((c) => ({ ...c, ceiling: e.target.value }))} className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 font-mono text-sm text-white focus:border-blue-400/60 focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">Format</span>
                    <select data-testid="master-adv-format" value={custom.format} onChange={(e) => setCustom((c) => ({ ...c, format: e.target.value }))} className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white focus:border-blue-400/60 focus:outline-none">
                      {EXPORT_FORMATS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                    </select>
                  </label>
                  {stats ? (
                    <p data-testid="master-adv-readout" className="font-mono text-[11px] text-white/55 sm:col-span-2">
                      Before {fmtDb(stats.before.lufs)} LUFS · TP {fmtDb(stats.before.truePeakDb)} dB → After {fmtDb(stats.after.lufs)} LUFS · TP {fmtDb(stats.after.truePeakDb)} dB
                    </p>
                  ) : <span className="sm:col-span-2" />}
                  <button type="button" data-testid="master-adv-apply" disabled={!!busy || loadState !== "ready"} onClick={applyCustom} className="rounded-full border border-white/15 bg-white/[0.06] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-white transition-colors hover:bg-white/[0.12] disabled:opacity-50">
                    {busy === "custom" ? "Applying…" : "Apply custom settings"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5">
              <div className="flex items-center gap-3">
                <SongCover song={song} size="md" />
                <div className="min-w-0"><p className="truncate font-display text-base font-semibold">{song.title}</p><p className="text-[12px] text-white/50">{song.creator_meta?.style_label || "Your song"}</p></div>
              </div>
              <button type="button" data-testid="master-export-btn" disabled={!stats || !!busy} onClick={exportMaster} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-violet-600 px-6 py-4 font-display text-base font-bold text-white shadow-[0_12px_36px_rgba(99,102,241,0.4)] transition-transform hover:scale-[1.02] disabled:opacity-40 disabled:hover:scale-100">
                {busy === "export" ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" />} Export mastered song
              </button>
              <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.16em] text-white/35">{(EXPORT_FORMATS.find((f) => f.key === custom.format) || EXPORT_FORMATS[0]).label}</p>
              <button type="button" data-testid="master-keep-original" disabled={!stats || !!busy} onClick={keepOriginal} className="mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white/70 transition-colors hover:bg-white/[0.1] hover:text-white disabled:opacity-40">
                <RotateCcw className="h-3.5 w-3.5" /> Keep the original sound
              </button>
              <Link to={`/create/song/${song.id}`} data-testid="master-back-to-song" className="mt-4 block text-center font-mono text-[11px] uppercase tracking-[0.16em] text-white/45 hover:text-white">Back to your song</Link>
            </div>
          </aside>
        </div>
      )}
    </CreatorShell>
  );
}
